/**
 * Theme configuration for Verint Developer Portal
 * 
 * This module defines light and dark theme variants with their providers.
 * Each theme object includes metadata (id, title, variant) and a Provider component.
 */

import type { ReactNode } from 'react';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
} from '@backstage/theme';
import { pageThemeConfig } from './customization';

const INTER_FAMILY = "'Inter', Arial, sans-serif";

/**
 * Light theme for the portal
 */
const myTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    navigation: {
      ...palettes.light.navigation,
      background: '#ffffff',
      color: '#1d1d1f',
      selectedColor: '#0078d4',
      indicator: '#0078d4',
    },
  },
  fontFamily: INTER_FAMILY,
  pageTheme: {
    home: genPageTheme({ colors: pageThemeConfig.colors, shape: pageThemeConfig.shapes.wave }),
    documentation: genPageTheme({ colors: ['#f7f8fa', '#f7f8fa'], shape: pageThemeConfig.shapes.wave }),
    tool: genPageTheme({ colors: ['#f7f8fa', '#f7f8fa'], shape: pageThemeConfig.shapes.round }),
    other: genPageTheme({ colors: ['#f7f8fa', '#f7f8fa'], shape: pageThemeConfig.shapes.wave }),
    app: genPageTheme({ colors: ['#f7f8fa', '#f7f8fa'], shape: pageThemeConfig.shapes.wave }),
    apis: genPageTheme({ colors: ['#f7f8fa', '#f7f8fa'], shape: pageThemeConfig.shapes.wave }),
  },
});

/**
 * Dark theme for the portal — Verint dark palette
 *
 * Color tokens derived from the Verint .ges-dark specification:
 *   text-default: #e4e6e8 | text-mid: #a0a4a8 | text-light: #6e7479
 *   bg-app: #1a1a1a | bg-panels: #2e2e2e | bg-chiclets: #414141
 *   border: #414141 | info: #4da3ff | warning: #ffb366 | error: #ff4d4d
 */
const myDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    background: {
      default: '#1a1a1a',
      paper: '#2e2e2e',
    },
    navigation: {
      ...palettes.dark.navigation,
      background: '#1e1e1e',
      color: '#e4e6e8',
      selectedColor: '#ffffff',
      indicator: '#0078d4',
    },
    text: {
      primary: '#e4e6e8',
      secondary: '#a0a4a8',
      disabled: '#6e7479',
    },
    divider: '#414141',
    info: {
      main: '#4da3ff',
    },
    warning: {
      main: '#ffb366',
    },
    error: {
      main: '#ff4d4d',
    },
    action: {
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
      focus: 'rgba(77, 163, 255, 0.24)',
    },
  },
  fontFamily: INTER_FAMILY,
  pageTheme: {
    home: genPageTheme({ colors: ['#1a1a1a', '#1a1a1a'], shape: pageThemeConfig.shapes.wave }),
    documentation: genPageTheme({ colors: ['#1a1a1a', '#1a1a1a'], shape: pageThemeConfig.shapes.wave }),
    tool: genPageTheme({ colors: ['#1a1a1a', '#1a1a1a'], shape: pageThemeConfig.shapes.round }),
    other: genPageTheme({ colors: ['#1a1a1a', '#1a1a1a'], shape: pageThemeConfig.shapes.wave }),
    app: genPageTheme({ colors: ['#1a1a1a', '#1a1a1a'], shape: pageThemeConfig.shapes.wave }),
    apis: genPageTheme({ colors: ['#1a1a1a', '#1a1a1a'], shape: pageThemeConfig.shapes.wave }),
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#2e2e2e',
          borderColor: '#414141',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#414141',
          color: '#e4e6e8',
        },
        outlined: {
          borderColor: '#414141',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#414141',
          '&:hover': {
            borderColor: '#9aa2ab',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            color: '#6e7479',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#414141',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9aa2ab',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4da3ff',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: '#e4e6e8',
        },
        input: {
          '&::placeholder': {
            color: '#6e7479',
            opacity: 1,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#414141',
        },
      },
    },
  },
});

/**
 * Light theme configuration object for createApp()
 */
export const lightTheme = {
  id: 'light',
  title: 'Light Theme',
  variant: 'light' as const,
  Provider: ({ children }: { children: ReactNode }) => (
    <UnifiedThemeProvider theme={myTheme}>{children}</UnifiedThemeProvider>
  ),
};

/**
 * Dark theme configuration object for createApp()
 */
export const darkTheme = {
  id: 'dark',
  title: 'Dark Theme',
  variant: 'dark' as const,
  Provider: ({ children }: { children: ReactNode }) => (
    <UnifiedThemeProvider theme={myDarkTheme}>{children}</UnifiedThemeProvider>
  ),
};

/**
 * Array of all available themes
 */
export const themes = [lightTheme, darkTheme];
